// src/screens/MembershipsScreen.js
// ============================================================================
// YOUR MEMBERSHIPS (evergreen 3.5a, C4): the viewer's channel memberships,
// and team memberships (evergreen 3.5b).
//
// One place to see, cancel, keep and fix every channel and team membership,
// plus a link to the Plans page, so one entry reaches both cancel paths.
// Rows are keyed `${kind}:${targetId}` (a channel row's targetId is its
// creatorUid), and every action passes {kind, targetId}. Exempt from the
// Terms prompt (App.js HIDE_TOS_PROMPT_ROUTES): managing, cancelling and
// paying never wait on accepting new Terms.
//
// Data: listMyMemberships (functions/modules/memberships.js, 5.3) returns the
// payer's own rows: status, dates in ms, their own price, the perks they get
// (already cut to what a removed member keeps), canCancel / canResume, and the
// hosted invoice link for a payment that needs attention. After ANY failed
// change the list is fetched again, so the screen never shows a stale state.
//
// Copy: "Join" / "Member" / "Membership" only ("Subscribe" is Follow and the
// Plans button). The payer sees only their own price. No em dashes.
//
// Tiers (R5, 5.11 F5): each row shows "{Tier} · {price} a month" (the tier
// and price of the month paid for), a pending switch's line, "Switch tier"
// (canSwitch: a tier to switch to) and, while a switch is pending, "Keep
// {From}" (canKeep; an older server sends only canSwitch). Both open the
// Join modal's switch stage, which reads the server's switch options.
// ============================================================================

import React, { useCallback, useEffect, useState } from 'react';
import {
  View, Text, ScrollView, TouchableOpacity, ActivityIndicator, StyleSheet, SafeAreaView, Modal, Pressable,
  Platform, Linking, Switch, Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebaseConfig';
import { THEME } from '../constants/uiTheme';
import { useError } from '../components/ErrorContext';
import Header from '../components/Header';
import ConsentCheckbox, { ConsentHint } from '../components/ConsentCheckbox';
import JoinMembershipModal from '../components/JoinMembershipModal';
import useMembershipsLive from '../hooks/useMembershipsLive';
import {
  listMyMemberships,
  cancelMyMembership,
  resumeMyMembership,
  setMyMembershipPrefs,
  membershipErrorKind,
  membershipErrorMessage,
  logMembershipError,
  formatUsd,
  formatMonthlyPrice,
  formatMembershipDate,
  LISTED_PERKS_BY_KIND,
  PERK_LABELS_BY_KIND,
  teamDisplayName,
} from '../utils/membershipPayment';
import {
  tierNameOf, tierPriceLine, pendingSwitchLine, longDate, keepTierLabel, SWITCH_TIER_LABEL,
} from '../utils/membershipTiers';

const C = THEME.colors;
const IS_WEB = Platform.OS === 'web';
// Pay buttons open only Stripe's hosted invoice page; anything else gets the
// status line + "Try again".
const INVOICE_URL_PREFIX = 'https://invoice.stripe.com/';
const ERROR_TITLE = 'Your memberships';

const fmtDate = formatMembershipDate; // "Oct 26, 2026"

// The server's date for the recorded Keep text: "October 26, 2026" in
// America/Chicago (memberships.js longDate), not the row's short local date.
const keepDate = (ms) => new Date(Number(ms) || 0).toLocaleDateString('en-US', {
  month: 'long', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago',
});

// Mirrors of the server's texts (memberships.js keepDisclosureText /
// consentText): the words beside the box are the words the server records.
export function keepDisclosureText(cents, dateMs) {
  return `Your membership renews on ${keepDate(dateMs)} at ${formatUsd(cents)} a month until you cancel. `
    + 'Cancel anytime in Your memberships; your perks stay until the end of the month you paid for.';
}
export function consentText(cents) {
  return `I agree that my membership renews automatically at ${formatUsd(cents)} every month until I cancel in Your memberships.`;
}

export const payUrlAllowed = (url) => typeof url === 'string' && url.startsWith(INVOICE_URL_PREFIX);

const hasPaymentProblem = (row) => row.status === 'past_due' || row.status === 'unpaid'
  || row.needsPayment === true || row.actionNeeded === true;

// The current period is unpaid (past due, unpaid, or the renewal date has come
// and gone): a cancel ends it now instead of at period end (5.4).
export function periodUnpaid(row, nowMs = Date.now()) {
  if (row.status === 'past_due' || row.status === 'unpaid') return true;
  return Number(row.renewsAtMs) > 0 && Number(row.renewsAtMs) <= nowMs;
}

// One status line per row. Never "Ends {date}" for a date already past.
export function statusLineFor(row, nowMs = Date.now()) {
  const s = row.status;
  if (s === 'activating') return 'Finishing up';
  if (s === 'attempt_failed') return "Your last payment didn't go through.";
  if (s === 'ended') return row.endedAtMs ? `Ended ${fmtDate(row.endedAtMs)}` : 'Ended';
  if (row.actionNeeded === true) return 'Your bank needs you to confirm this payment.';
  if (hasPaymentProblem(row)) return "Your payment didn't go through. Update your payment to keep your perks.";
  if (s === 'canceling') {
    const end = Number(row.endsAtMs) || 0;
    if (end > nowMs) return `Ends ${fmtDate(end)}. You won't be charged again.`;
    return end ? `Ended ${fmtDate(end)}` : 'Ended';
  }
  if (Number(row.renewsAtMs) > 0) {
    const renews = `Renews ${fmtDate(row.renewsAtMs)}`;
    return Number(row.scheduledEndMs) > 0 ? `${renews}. Scheduled to end ${fmtDate(row.scheduledEndMs)}.` : renews;
  }
  return '';
}

// '/s/<encoded handle>' -> the StreamWatch param (the watch page resolves a
// handle or a uid). Falls back to the uid for an older server or a bad path.
export function channelParamOf(row) {
  const p = row?.channelPath;
  if (typeof p === 'string' && p.startsWith('/s/') && p.length > 3) {
    try { return decodeURIComponent(p.slice(3)); } catch (_) { /* fall through */ }
  }
  return row?.creatorUid || null;
}

// A row's kind and target (an older server's channel rows carry neither, so
// they fall back to the channel and its creatorUid).
export const kindOf = (row) => (row?.kind === 'team' ? 'team' : 'channel');
export const targetIdOf = (row) => (kindOf(row) === 'team'
  ? (row?.targetId || null)
  : (row?.targetId || row?.creatorUid || null));
export const rowKeyOf = (row) => `${kindOf(row)}:${targetIdOf(row)}`;

// The name for SENTENCES ("Cancel your membership to {name}?").
export function nameOf(row) {
  if (kindOf(row) === 'team') return teamDisplayName(row?.targetName);
  if (row?.creatorName) return String(row.creatorName);
  return row?.targetName ? String(row.targetName) : 'this channel';
}

// The card TITLE: the same name, with the title fallbacks 'A team' and
// 'A channel' (never the sentence fallback 'this team').
export function titleNameOf(row) {
  if (kindOf(row) === 'team') {
    return String(row?.targetName || '').trim() ? teamDisplayName(row.targetName) : 'A team';
  }
  return row?.creatorName || 'A channel';
}

// Empty state, keyed on the (channel live, team live) pair.
export function emptyStateText(live, teamLive) {
  if (live && teamLive) return 'No memberships yet. When you join a channel or a team, it shows up here.';
  if (live) return 'No memberships yet. When you join a channel, it shows up here.';
  if (teamLive) return 'No memberships yet. When you join a team, it shows up here.';
  return "You don't have any memberships.";
}

export default function MembershipsScreen({ navigation }) {
  const { showError } = useError();
  const { live: membershipsLive, teamLive } = useMembershipsLive();
  const [authReady, setAuthReady] = useState(false);
  const [user, setUser] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  // 'cancel:<kind>:<targetId>' | 'keep:<kind>:<targetId>' |
  // 'badge:<kind>:<targetId>' while a call is in flight.
  const [busy, setBusy] = useState(null);
  const [cancelFor, setCancelFor] = useState(null); // row
  const [keepFor, setKeepFor] = useState(null); // { row, amountCents, checked }
  // The switch stage (R5): { creatorUid, creatorName, openTo: 'switch'|'stay' }.
  const [switchFor, setSwitchFor] = useState(null);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, (u) => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await listMyMemberships();
      setRows(Array.isArray(data?.memberships) ? data.memberships : []);
    } catch (err) {
      console.error('[MembershipsScreen] list failed:', err);
      setLoadError(membershipErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authReady && user) load();
    if (authReady && !user) setRows([]);
  }, [authReady, user, load]);

  // A team row opens its public team page; a channel row its watch page.
  const openTarget = (row) => {
    if (kindOf(row) === 'team') {
      const teamId = targetIdOf(row);
      if (teamId) navigation.navigate('TeamPage', { teamId });
      return;
    }
    const channel = channelParamOf(row);
    if (channel) navigation.navigate('StreamWatch', { channel });
  };

  const openInvoice = (url) => {
    if (!payUrlAllowed(url)) return;
    if (IS_WEB && typeof window !== 'undefined') {
      const w = window.open(url, '_blank');
      if (w) { try { w.opener = null; } catch (_) { /* noop */ } return; }
    }
    Linking.openURL(url).catch(() => {});
  };

  // After ANY failed change: the server's sentence, the confirm closed, and the
  // list fetched again (its source of truth).
  const failed = (err) => {
    logMembershipError('MembershipsScreen change', err);
    showError(ERROR_TITLE, membershipErrorMessage(err));
    load();
  };

  const confirmCancel = async () => {
    const row = cancelFor;
    if (!row) return;
    setBusy(`cancel:${rowKeyOf(row)}`);
    try {
      const res = await cancelMyMembership({ kind: kindOf(row), targetId: targetIdOf(row) });
      setCancelFor(null);
      showError(
        'Membership canceled',
        res?.endedNow === true
          ? "Your membership has ended. You won't be charged again."
          : Number(res?.endsAtMs) > 0
            ? `Your perks stay until ${fmtDate(res.endsAtMs)}.`
            : 'Your perks stay until the end of the month you paid for.'
      );
      load();
    } catch (err) {
      setCancelFor(null);
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const confirmKeep = async () => {
    const k = keepFor;
    if (!k || !k.checked) return;
    setBusy(`keep:${rowKeyOf(k.row)}`);
    try {
      await resumeMyMembership({
        kind: kindOf(k.row),
        targetId: targetIdOf(k.row),
        renewalConsent: { accepted: true, amountCents: k.amountCents },
      });
      setKeepFor(null);
      load();
    } catch (err) {
      const kind = membershipErrorKind(err);
      const serverCents = Number(err?.details?.amountCents);
      if (kind === 'price_changed' && serverCents > 0) {
        // Re-render the confirm with the server's price, unticked, under the
        // server's sentence so the change is never silent.
        setKeepFor((prev) => (prev ? { ...prev, amountCents: serverCents, checked: false, notice: membershipErrorMessage(err) } : prev));
        load();
      } else if (kind === 'consent') {
        setKeepFor((prev) => (prev ? { ...prev, checked: false } : prev));
      } else {
        setKeepFor(null);
        failed(err);
      }
    } finally {
      setBusy(null);
    }
  };

  const toggleBadge = async (row, showBadge) => {
    const key = rowKeyOf(row);
    setBusy(`badge:${key}`);
    try {
      await setMyMembershipPrefs({ kind: kindOf(row), targetId: targetIdOf(row), showBadge });
      // By the row key, never by creatorUid (null on every team row).
      setRows((prev) => prev.map((r) => (rowKeyOf(r) === key ? { ...r, showBadge } : r)));
    } catch (err) {
      failed(err);
    } finally {
      setBusy(null);
    }
  };

  const renderRow = (row) => {
    const now = Date.now();
    const kind = kindOf(row);
    const isTeam = kind === 'team';
    const key = rowKeyOf(row);
    const live = row.status !== 'ended' && row.status !== 'attempt_failed';
    const perkLines = live
      ? LISTED_PERKS_BY_KIND[kind].filter((k) => row.perks?.[k] === true).map((k) => PERK_LABELS_BY_KIND[kind][k])
      : [];
    const badgeLabel = isTeam ? 'Show my team badge in squad chat' : 'Show my member badge in chat';
    const payUrl = row.actionNeeded === true ? row.actionUrl : row.paymentUrl;
    const showPay = live && (row.actionNeeded === true || row.needsPayment === true) && payUrlAllowed(payUrl);
    const showRetry = live && hasPaymentProblem(row) && !showPay;
    const priceCents = Number(row.priceCents) || 0;
    // Tiers (F5): the tier of the month paid for, and a pending switch.
    const tierName = row.tierName || tierNameOf(row.tierKey);
    const nextName = row.nextTierName || tierNameOf(row.nextTierKey);
    const pending = live && !!nextName && Number(row.nextPriceCents) > 0 && Number(row.nextTierAtMs) > 0;
    // Tiers are channel only (3.5b): a team row has no creatorUid and no
    // switch, so Switch tier and Keep {From} never open for it.
    const openSwitch = (openTo) => {
      if (isTeam || !row.creatorUid) return;
      setSwitchFor({ creatorUid: row.creatorUid, creatorName: row.creatorName || '', openTo });
    };
    return (
      <View key={key} style={styles.card}>
        <TouchableOpacity style={styles.cardTop} onPress={() => openTarget(row)} accessibilityRole="link">
          {row.avatarUrl
            ? <Image source={{ uri: row.avatarUrl }} style={styles.avatar} />
            : <View style={[styles.avatar, styles.avatarEmpty]}><Ionicons name={isTeam ? 'people' : 'person'} size={18} color="#666" /></View>}
          <Text style={styles.name} numberOfLines={1}>{titleNameOf(row)}</Text>
        </TouchableOpacity>

        {statusLineFor(row, now) ? (
          <Text style={[styles.status, hasPaymentProblem(row) && live && styles.statusWarn]}>{statusLineFor(row, now)}</Text>
        ) : null}
        {priceCents > 0 && row.status !== 'attempt_failed' ? (
          <Text style={styles.price}>{tierName ? tierPriceLine(tierName, priceCents) : formatMonthlyPrice(priceCents)}</Text>
        ) : null}
        {pending ? (
          <Text style={styles.pending}>{pendingSwitchLine(nextName, longDate(row.nextTierAtMs), row.nextPriceCents)}</Text>
        ) : null}
        {perkLines.length ? (
          <View style={styles.perks}>
            {perkLines.map((p) => <Text key={p} style={styles.perk}>{'• '}{p}</Text>)}
          </View>
        ) : null}

        {live && row.perks?.badge === true ? (
          <View style={styles.prefRow}>
            <Text style={styles.prefLabel}>{badgeLabel}</Text>
            <Switch
              value={row.showBadge !== false}
              onValueChange={(v) => toggleBadge(row, v)}
              disabled={busy === `badge:${key}`}
              trackColor={{ false: '#333', true: C.brand }}
              thumbColor={row.showBadge !== false ? '#000' : '#888'}
              accessibilityLabel={badgeLabel}
            />
          </View>
        ) : null}

        <View style={styles.actions}>
          {showPay ? (
            <TouchableOpacity style={styles.primaryBtn} onPress={() => openInvoice(payUrl)} accessibilityRole="button">
              <Text style={styles.primaryBtnText}>{row.actionNeeded === true ? 'Confirm payment' : 'Update payment'}</Text>
            </TouchableOpacity>
          ) : null}
          {showRetry ? (
            <TouchableOpacity style={styles.secondaryBtn} onPress={load} accessibilityRole="button">
              <Text style={styles.secondaryBtnText}>Try again</Text>
            </TouchableOpacity>
          ) : null}
          {!isTeam && row.canSwitch === true ? (
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => openSwitch('switch')} accessibilityRole="button">
              <Text style={styles.secondaryBtnText}>{SWITCH_TIER_LABEL}</Text>
            </TouchableOpacity>
          ) : null}
          {!isTeam && pending && (row.canKeep === true || (row.canKeep === undefined && row.canSwitch === true)) && tierName ? (
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => openSwitch('stay')} accessibilityRole="button">
              <Text style={styles.secondaryBtnText}>{keepTierLabel(tierName)}</Text>
            </TouchableOpacity>
          ) : null}
          {row.canResume === true ? (
            <TouchableOpacity
              style={styles.primaryBtn}
              onPress={() => setKeepFor({ row, amountCents: Number(row.renewPriceCents) || priceCents, checked: false, notice: null })}
              accessibilityRole="button"
            >
              <Text style={styles.primaryBtnText}>Keep my membership</Text>
            </TouchableOpacity>
          ) : null}
          {row.canCancel === true ? (
            <TouchableOpacity style={styles.secondaryBtn} onPress={() => setCancelFor(row)} accessibilityRole="button">
              <Text style={styles.cancelText}>Cancel membership</Text>
            </TouchableOpacity>
          ) : null}
          <TouchableOpacity style={styles.secondaryBtn} onPress={() => openTarget(row)} accessibilityRole="button">
            <Text style={styles.secondaryBtnText}>{isTeam ? 'View team' : 'View channel'}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  };

  const cancelUnpaid = cancelFor ? periodUnpaid(cancelFor) : false;
  // The server's Keep text dates from paidThroughMs (a dashboard cancel_at can
  // put endsAtMs earlier); endsAtMs is the fallback for an older server.
  const keepDateMs = keepFor
    ? (Number(keepFor.row.paidThroughMs) || Number(keepFor.row.endsAtMs) || Number(keepFor.row.renewsAtMs) || 0)
    : 0;

  return (
    <SafeAreaView style={styles.safe}>
      <Header navigation={navigation} />
      <ScrollView contentContainerStyle={styles.scroll}>
        <View style={styles.inner}>
          <Text style={styles.title}>Your memberships</Text>

          {!authReady ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : !user ? (
            <View style={styles.stateBlock}>
              <Ionicons name="lock-closed" size={36} color="#666" />
              <Text style={styles.stateText}>Sign in to manage your memberships.</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={() => navigation.navigate('Login')} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Sign in</Text>
              </TouchableOpacity>
            </View>
          ) : loading && !rows.length ? (
            <View style={styles.stateBlock}><ActivityIndicator color={C.brand} size="large" /></View>
          ) : loadError && !rows.length ? (
            <View style={styles.stateBlock}>
              <Text style={styles.stateText}>{loadError}</Text>
              <TouchableOpacity style={styles.primaryBtn} onPress={load} accessibilityRole="button">
                <Text style={styles.primaryBtnText}>Try again</Text>
              </TouchableOpacity>
            </View>
          ) : !rows.length ? (
            <View style={styles.stateBlock}>
              <Ionicons name="star-outline" size={36} color="#666" />
              <Text style={styles.stateText}>
                {emptyStateText(membershipsLive === true, teamLive === true)}
              </Text>
            </View>
          ) : (
            rows.map(renderRow)
          )}

          {authReady && user ? (
            <TouchableOpacity style={styles.planLink} onPress={() => navigation.navigate('Subscription')} accessibilityRole="link">
              <Ionicons name="card-outline" size={16} color={C.brand} style={{ marginRight: 8 }} />
              <Text style={styles.planLinkText}>Your plan</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </ScrollView>

      {/* Switch tier / Keep {From} (R5): the Join modal's switch stage, keyed
          by channel like every other mount; the list reloads after a switch
          and on close. */}
      {switchFor ? (
        <JoinMembershipModal
          key={'join:' + switchFor.creatorUid}
          creatorUid={switchFor.creatorUid}
          creatorName={switchFor.creatorName}
          visible
          openTo={switchFor.openTo}
          onChanged={load}
          onClose={() => { setSwitchFor(null); load(); }}
          navigation={navigation}
        />
      ) : null}

      {/* Cancel confirm. An unpaid period ends now (nothing more is charged). */}
      <Modal visible={!!cancelFor} transparent animationType="fade" onRequestClose={() => setCancelFor(null)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setCancelFor(null)} />
          <View style={styles.dgCard}>
            <Text style={styles.dgTitle}>{`Cancel your membership to ${nameOf(cancelFor)}?`}</Text>
            <Text style={styles.dgBody}>
              {cancelUnpaid
                ? "Your membership ends now. You won't be charged again."
                : Number(cancelFor?.renewsAtMs) > 0
                  ? `You keep your perks until ${fmtDate(cancelFor.renewsAtMs)}. You won't be charged again.`
                  : "You keep your perks until the end of the month you paid for. You won't be charged again."}
            </Text>
            <View style={styles.dgBtnRow}>
              <TouchableOpacity style={styles.dgCancel} onPress={() => setCancelFor(null)} accessibilityRole="button">
                <Text style={styles.dgCancelTxt}>Keep membership</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={styles.dgDanger}
                onPress={confirmCancel}
                disabled={!!busy}
                accessibilityRole="button"
              >
                {busy && busy.startsWith('cancel:')
                  ? <ActivityIndicator size="small" color="#fff" />
                  : <Text style={styles.dgDangerTxt}>Cancel membership</Text>}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>

      {/* Keep confirm: Keep restarts automatic renewal, so it takes the same
          express consent as a join, for the exact price charged. */}
      <Modal visible={!!keepFor} transparent animationType="fade" onRequestClose={() => setKeepFor(null)}>
        <View style={styles.dgOverlay}>
          <Pressable style={styles.dgScrim} onPress={() => setKeepFor(null)} />
          <View style={styles.dgCard}>
            <Text style={styles.dgTitle}>{`Keep your membership to ${nameOf(keepFor?.row)}?`}</Text>
            {keepFor ? (
              <>
                {keepFor.notice ? <Text style={styles.keepNotice}>{keepFor.notice}</Text> : null}
                <Text style={styles.dgBody}>{keepDisclosureText(keepFor.amountCents, keepDateMs)}</Text>
                <ConsentCheckbox
                  checked={keepFor.checked === true}
                  onToggle={(next) => setKeepFor((prev) => (prev ? { ...prev, checked: next === true } : prev))}
                  label={consentText(keepFor.amountCents)}
                  disabled={!!busy}
                />
              </>
            ) : null}
            <View style={[styles.dgBtnRow, { marginTop: 14 }]}>
              <TouchableOpacity style={styles.dgCancel} onPress={() => setKeepFor(null)} accessibilityRole="button">
                <Text style={styles.dgCancelTxt}>Not now</Text>
              </TouchableOpacity>
              <TouchableOpacity
                style={[styles.dgConfirm, !(keepFor?.checked === true) && styles.btnDisabled]}
                onPress={confirmKeep}
                disabled={!(keepFor?.checked === true) || !!busy}
                accessibilityRole="button"
                accessibilityState={{ disabled: !(keepFor?.checked === true) }}
              >
                {busy && busy.startsWith('keep:')
                  ? <ActivityIndicator size="small" color="#000" />
                  : <Text style={styles.dgConfirmTxt}>Keep renewing</Text>}
              </TouchableOpacity>
            </View>
            <ConsentHint visible={!!keepFor && keepFor.checked !== true} style={styles.tickHint} />
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1, backgroundColor: C.bg },
  scroll: { padding: 16, paddingBottom: 48 },
  inner: { width: '100%', maxWidth: 760, alignSelf: 'center' },
  title: { color: C.text, fontSize: 26, fontWeight: '800', marginBottom: 16 },

  stateBlock: { alignItems: 'center', paddingVertical: 48, paddingHorizontal: 16, gap: 12 },
  stateText: { color: C.textMuted, fontSize: 14, textAlign: 'center', maxWidth: 420, lineHeight: 20 },

  card: {
    backgroundColor: C.surface, borderWidth: 1, borderColor: C.border,
    borderRadius: THEME.radius.lg, padding: 16, marginBottom: 12,
  },
  cardTop: { flexDirection: 'row', alignItems: 'center', marginBottom: 8 },
  avatar: { width: 36, height: 36, borderRadius: 18, marginRight: 10, backgroundColor: C.chip },
  avatarEmpty: { alignItems: 'center', justifyContent: 'center' },
  name: { flex: 1, minWidth: 0, color: C.text, fontSize: 16, fontWeight: '800' },
  status: { color: C.text, fontSize: 13.5, lineHeight: 20 },
  statusWarn: { color: '#ff9c9c' },
  price: { color: C.brand, fontSize: 13.5, fontWeight: '700', marginTop: 4 },
  pending: { color: C.text, fontSize: 13, lineHeight: 19, marginTop: 4 },
  perks: { marginTop: 8 },
  perk: { color: C.textMuted, fontSize: 13, lineHeight: 20 },
  prefRow: {
    flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    marginTop: 10, paddingTop: 10, borderTopWidth: 1, borderTopColor: C.border,
  },
  prefLabel: { flex: 1, color: C.text, fontSize: 13.5 },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8, marginTop: 12 },
  primaryBtn: {
    backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingHorizontal: 16, paddingVertical: 10, alignItems: 'center',
  },
  primaryBtnText: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  secondaryBtn: {
    backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingHorizontal: 16, paddingVertical: 10, alignItems: 'center',
  },
  secondaryBtnText: { color: C.text, fontWeight: '700', fontSize: 13.5 },
  cancelText: { color: '#ff6b6b', fontWeight: '700', fontSize: 13.5 },
  planLink: { flexDirection: 'row', alignItems: 'center', alignSelf: 'flex-start', marginTop: 12, paddingVertical: 8 },
  planLinkText: { color: C.brand, fontSize: 14, fontWeight: '700' },

  dgOverlay: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 20 },
  dgScrim: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.75)' },
  dgCard: {
    width: '100%', maxWidth: 400, backgroundColor: C.surface, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.lg, padding: 20,
  },
  dgTitle: { color: C.text, fontSize: 17, fontWeight: '800', marginBottom: 10 },
  dgBody: { color: C.textMuted, fontSize: 13.5, lineHeight: 20, marginBottom: 10 },
  dgBtnRow: { flexDirection: 'row', gap: 10 },
  dgCancel: {
    flex: 1, backgroundColor: C.chip, borderWidth: 1, borderColor: C.borderStrong,
    borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center',
  },
  dgCancelTxt: { color: C.text, fontWeight: '700', fontSize: 13.5 },
  dgConfirm: { flex: 1, backgroundColor: C.brand, borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgConfirmTxt: { color: '#000', fontWeight: '800', fontSize: 13.5 },
  dgDanger: { flex: 1, backgroundColor: '#e5484d', borderRadius: THEME.radius.md, paddingVertical: 12, alignItems: 'center' },
  dgDangerTxt: { color: '#fff', fontWeight: '800', fontSize: 13.5 },
  btnDisabled: { opacity: 0.45 },
  tickHint: { color: C.textMuted, fontSize: 12, marginTop: 8, textAlign: 'right' },
  keepNotice: { color: '#f0a040', fontSize: 12.5, lineHeight: 18, marginBottom: 10 },
});
